import { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import type { ChangeItem, Feature, FeatureCollection } from './api';

export type LayerKey = 'osm' | 'topo' | 'satellite';

export const LAYERS: Record<LayerKey, { url: string; attribution: string; maxZoom: number }> = {
  osm: {
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '© OpenStreetMap contributors',
    maxZoom: 19,
  },
  topo: {
    url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
    attribution: '© OpenTopoMap (CC-BY-SA)',
    maxZoom: 17,
  },
  satellite: {
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: '© Esri World Imagery',
    maxZoom: 19,
  },
};

const CHANGE_COLOR: Record<string, string> = { add: '#2e7d32', update: '#ef6c00', delete: '#c62828' };
const SELECTED_COLOR = '#e65100';

/**
 * Neutral colour for master features. Deliberately not the review
 * add/update/delete hues (green/orange/red) so the standing map is never
 * confused with a set of proposed changes (US-3.8).
 */
const MASTER_COLOR = '#1565c0';

function esc(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** A popup that shows every attribute the feature carries, not just name/type. */
function popupHtml(props: Record<string, unknown>, missingNameLabel: string): string {
  const name = typeof props.name === 'string' && props.name.trim() ? props.name : missingNameLabel;
  const lines: string[] = [`<strong>${esc(name)}</strong>`];
  const add = (label: string, value: unknown) => {
    if (value === undefined || value === null || value === '') return;
    lines.push(`${esc(label)}: ${esc(String(value))}`);
  };
  add('type', props.type);
  add('description', props.description);
  add('symbol', props.symbol);
  add('elevation_m', props.elevation_m);
  add('observed_at', props.observed_at);
  const attrs = props.attrs;
  if (attrs && typeof attrs === 'object') {
    for (const [key, value] of Object.entries(attrs as Record<string, unknown>)) add(key, value);
  }
  return lines.join('<br/>');
}

/** Bind a permanent label so a feature's name is readable without a click. */
function bindLabel(
  layer: L.Layer,
  name: string | undefined,
  className: string,
  offset: [number, number] = [6, 0],
): void {
  const text = String(name ?? '').trim();
  if (!text) return;
  layer.bindTooltip(esc(text), { permanent: true, direction: 'right', className, offset });
}

/** A high-contrast circular badge for "?" / "!" map annotations. */
function annotationBadge(className: string, glyph: string): L.DivIcon {
  return L.divIcon({
    className,
    html: `<span aria-hidden="true">${glyph}</span>`,
    iconSize: [20, 20],
    // Sit just above the feature so its marker (and selection) stays visible.
    iconAnchor: [10, 22],
  });
}

/** A feature counts as named only when it carries a non-blank name. */
function hasName(props: Record<string, unknown> | null | undefined): boolean {
  return typeof props?.name === 'string' && props.name.trim().length > 0;
}

function featureOf(geometry: unknown, id: string | null, name: unknown, action: string) {
  return {
    type: 'Feature',
    id: id ?? undefined,
    geometry,
    properties: { name: typeof name === 'string' ? name : undefined, action },
  } as unknown as GeoJSON.Feature;
}

export function MapView({
  data,
  changes,
  layer,
  missingNameLabel,
  candidates,
  candidateLabel,
  onFeatureClick,
  editable,
  onMapClick,
  selectedFeatureId,
  fitKey,
  vertices,
  selectedVertex,
  onVertexDrag,
  onVertexClick,
  previewLine,
  highlightKey,
  focusKey,
  focusGeometry,
  onChangeClick,
}: {
  data: FeatureCollection | null;
  changes?: ChangeItem[];
  layer: LayerKey;
  /** Localised text for a feature that has no name (map label and popup title). */
  missingNameLabel: string;
  /** Existing master features that make the open row an ambiguous match. */
  candidates?: Feature[];
  candidateLabel?: string;
  onFeatureClick?: (feature: { id: string; name?: string }) => void;
  editable?: boolean;
  onMapClick?: (latlng: { lat: number; lng: number }) => void;
  selectedFeatureId?: string | null;
  /** Refit the viewport only when this changes (so editing never re-zooms). */
  fitKey?: string | null;
  /** Editable vertices of the selected line (or a line being drawn), as [lng, lat]. */
  vertices?: Array<[number, number]>;
  selectedVertex?: number | null;
  onVertexDrag?: (index: number, lngLat: { lat: number; lng: number }) => void;
  onVertexClick?: (index: number) => void;
  /** A dashed preview while a new line is being drawn, as [lng, lat]. */
  previewLine?: Array<[number, number]>;
  /** The change item whose details are open: highlighted on the map. */
  highlightKey?: string | null;
  /** Bump to pan/zoom to `focusGeometry` (the opened change). */
  focusKey?: string | null;
  focusGeometry?: unknown;
  /** Clicking a rendered change opens its detail row. */
  onChangeClick?: (itemKey: string) => void;
}) {
  const container = useRef<HTMLDivElement>(null);
  const map = useRef<L.Map | null>(null);
  const tiles = useRef<L.TileLayer | null>(null);
  const drawn = useRef<L.FeatureGroup | null>(null);
  const clickHandler = useRef(onFeatureClick);
  const mapClickHandler = useRef(onMapClick);
  const vertexDragHandler = useRef(onVertexDrag);
  const vertexClickHandler = useRef(onVertexClick);
  const changeClickHandler = useRef(onChangeClick);
  const vertexLayer = useRef<L.LayerGroup | null>(null);
  const previewLayer = useRef<L.Polyline | null>(null);
  const fittedFor = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    clickHandler.current = onFeatureClick;
    mapClickHandler.current = onMapClick;
    vertexDragHandler.current = onVertexDrag;
    vertexClickHandler.current = onVertexClick;
    changeClickHandler.current = onChangeClick;
  });

  useEffect(() => {
    if (!container.current || map.current) return;
    const instance = L.map(container.current, { zoomControl: true }).setView([64.3, 17.0], 10);
    map.current = instance;
    const onMapClickEvent = (e: L.LeafletMouseEvent) =>
      mapClickHandler.current?.({ lat: e.latlng.lat, lng: e.latlng.lng });
    instance.on('click', onMapClickEvent);
    return () => {
      instance.off('click', onMapClickEvent);
      instance.remove();
      map.current = null;
    };
  }, []);

  useEffect(() => {
    container.current?.classList.toggle('map-editing', Boolean(editable));
  }, [editable]);

  useEffect(() => {
    if (!map.current) return;
    if (tiles.current) map.current.removeLayer(tiles.current);
    const cfg = LAYERS[layer];
    tiles.current = L.tileLayer(cfg.url, { attribution: cfg.attribution, maxZoom: cfg.maxZoom }).addTo(map.current);
  }, [layer]);

  // Draggable vertex handles for the selected line (or a line being drawn).
  useEffect(() => {
    const instance = map.current;
    if (!instance) return;
    if (vertexLayer.current) {
      instance.removeLayer(vertexLayer.current);
      vertexLayer.current = null;
    }
    if (!editable || !vertices || vertices.length === 0) return;
    const group = L.layerGroup();
    vertices.forEach((coord, index) => {
      const marker = L.marker([coord[1], coord[0]], {
        draggable: Boolean(vertexDragHandler.current),
        keyboard: false,
        icon: L.divIcon({
          className: `vertex-handle${index === selectedVertex ? ' selected' : ''}`,
          iconSize: [12, 12],
        }),
      });
      marker.on('dragend', () => {
        const ll = marker.getLatLng();
        vertexDragHandler.current?.(index, { lat: ll.lat, lng: ll.lng });
      });
      marker.on('click', (e) => {
        L.DomEvent.stopPropagation(e);
        vertexClickHandler.current?.(index);
      });
      marker.addTo(group);
    });
    group.addTo(instance);
    vertexLayer.current = group;
  }, [vertices, selectedVertex, editable]);

  // A dashed preview while a new line is being drawn.
  useEffect(() => {
    const instance = map.current;
    if (!instance) return;
    if (previewLayer.current) {
      instance.removeLayer(previewLayer.current);
      previewLayer.current = null;
    }
    if (!previewLine || previewLine.length < 2) return;
    previewLayer.current = L.polyline(
      previewLine.map(([lng, lat]) => [lat, lng] as [number, number]),
      { color: SELECTED_COLOR, weight: 2, dashArray: '6 4' },
    ).addTo(instance);
  }, [previewLine]);

  // Pan/zoom to the change whose detail is open (US-7.2). Keep enough context:
  // do not zoom in past 13 for a point or 14 for a line/area, and never zoom out
  // if the reviewer is already closer.
  useEffect(() => {
    const instance = map.current;
    if (!instance || !focusKey || !focusGeometry) return;
    const geometry = focusGeometry as { type?: string; coordinates?: unknown };
    if (geometry.type === 'Point' && Array.isArray(geometry.coordinates)) {
      const [lon, lat] = geometry.coordinates as [number, number];
      instance.flyTo([lat, lon], Math.max(instance.getZoom(), 13), { duration: 0.4 });
      return;
    }
    const bounds = L.geoJSON(focusGeometry as GeoJSON.GeoJsonObject).getBounds();
    if (bounds.isValid()) instance.flyToBounds(bounds, { padding: [80, 80], maxZoom: 14, duration: 0.4 });
    // Only react to opening a different change; reloading the same detail must
    // not yank the map around.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusKey]);

  useEffect(() => {
    if (!map.current) return;
    if (drawn.current) map.current.removeLayer(drawn.current);
    const group = L.featureGroup();
    const review = Boolean(changes && changes.length > 0);
    const candidateIds = new Set((candidates ?? []).map((candidate) => candidate.id));
    const isSelected = (feature: GeoJSON.Feature) =>
      Boolean(editable) && typeof feature.id === 'string' && feature.id === selectedFeatureId;

    if (data) {
      L.geoJSON(data as unknown as GeoJSON.GeoJsonObject, {
        style: (feature) =>
          isSelected(feature as GeoJSON.Feature)
            ? { color: SELECTED_COLOR, weight: 4, opacity: 1 }
            : review
              ? { color: '#9e9e9e', weight: 2, opacity: 0.5 }
              : { color: MASTER_COLOR, weight: 2.5, opacity: 0.85 },
        pointToLayer: (feature, latlng) => {
          const unnamed = !hasName(feature.properties);
          const isCandidate = typeof feature.id === 'string' && candidateIds.has(feature.id);
          if (unnamed && !isCandidate) {
            L.marker(latlng, {
              icon: annotationBadge('missing-name-badge', '!'),
              interactive: false,
              keyboard: false,
            }).addTo(group);
          }
          return L.circleMarker(
            latlng,
            isSelected(feature)
              ? { radius: 8, color: SELECTED_COLOR, fillColor: '#ffb74d', fillOpacity: 1, weight: 2 }
              : review
                ? { radius: 4, color: '#9e9e9e', fillColor: '#bdbdbd', fillOpacity: 0.6, weight: 1 }
                : { radius: 5, color: '#0d47a1', fillColor: '#64b5f6', fillOpacity: 0.9, weight: 1.5 },
          );
        },
        onEachFeature: (feature, lyr) => {
          const p = (feature.properties ?? {}) as Record<string, unknown>;
          const name = typeof p.name === 'string' ? p.name : undefined;
          const isCandidate = typeof feature.id === 'string' && candidateIds.has(feature.id);
          const point = (feature.geometry as GeoJSON.Geometry | null)?.type === 'Point';
          const unnamed = !hasName(p);
          // Keep the badge clearest: the "?" candidate badge sits at the point,
          // and an unnamed point has its own "!" badge.
          const offset: [number, number] = isCandidate ? [16, 0] : unnamed && point ? [14, 0] : [6, 0];
          if (unnamed) {
            bindLabel(lyr, missingNameLabel, 'map-label missing-name', offset);
          } else {
            bindLabel(lyr, name, `map-label${review ? ' muted' : ''}`, offset);
          }
          lyr.bindPopup(popupHtml(p, missingNameLabel));
          if (typeof feature.id === 'string') {
            const id = feature.id;
            lyr.on('click', (e) => {
              if (editable) L.DomEvent.stopPropagation(e);
              clickHandler.current?.({ id, name });
            });
          }
        },
      }).addTo(group);
    }

    for (const change of changes ?? []) {
      const source = change.after ?? change.before;
      const geometry = source?.geometry;
      if (!geometry) continue;
      const color = CHANGE_COLOR[change.action] ?? '#333';
      const highlighted = change.itemKey === highlightKey;
      const unnamed = !hasName(source?.properties);
      L.geoJSON(featureOf(geometry, change.featureId, source?.properties?.name, change.action), {
        style: (feature) => {
          const isPoint = (feature as GeoJSON.Feature).geometry?.type === 'Point';
          return {
            color,
            weight: isPoint ? 2 : highlighted ? 5 : change.action === 'delete' ? 2 : 3,
            dashArray: !isPoint && change.action === 'delete' ? '6 4' : undefined,
            opacity: 1,
          };
        },
        pointToLayer: (_f, latlng) => {
          if (unnamed) {
            L.marker(latlng, {
              icon: annotationBadge('missing-name-badge', '!'),
              interactive: false,
              keyboard: false,
            }).addTo(group);
          }
          return L.circleMarker(latlng, { radius: 6, color, fillColor: color, fillOpacity: 0.95, weight: 2 });
        },
        onEachFeature: (feature, lyr) => {
          const p = (feature.properties ?? {}) as { name?: string; action?: string };
          if (unnamed) {
            const point = (feature.geometry as GeoJSON.Geometry | null)?.type === 'Point';
            bindLabel(lyr, missingNameLabel, 'map-label missing-name', point ? [14, 0] : [6, 0]);
          } else {
            bindLabel(lyr, p.name, `map-label ${change.action}`);
          }
          if (highlighted && (feature.geometry as GeoJSON.Geometry | null)?.type === 'Point') {
            // Keep the original core (size/colour) and draw a thin outer ring.
            L.circleMarker((lyr as L.CircleMarker).getLatLng(), {
              radius: 9,
              color: '#111',
              weight: 2,
              fillOpacity: 0,
              interactive: false,
            }).addTo(group);
          }
          lyr.on('click', (e) => {
            L.DomEvent.stopPropagation(e);
            changeClickHandler.current?.(change.itemKey);
          });
        },
      }).addTo(group);
    }

    // Ambiguous-match candidates: a "?" badge on the master features a new row
    // could be a duplicate of, plus a label so they can be identified.
    if (candidates && candidateLabel) {
      candidates.forEach((candidate, index) => {
        const geometry = candidate.geometry;
        if (geometry?.type !== 'Point' || !Array.isArray(geometry.coordinates)) return;
        const [lon, lat] = geometry.coordinates as [number, number];
        if (!Number.isFinite(lon) || !Number.isFinite(lat)) return;
        const badge = L.marker([lat, lon], {
          icon: annotationBadge('candidate-badge', '?'),
          interactive: false,
          keyboard: false,
        }).addTo(group);
        badge.bindTooltip(`${candidateLabel} ${index + 1}`, {
          permanent: true,
          direction: 'left',
          className: 'map-label candidate',
          offset: [-14, 0],
        });
      });
    }

    group.addTo(map.current);
    drawn.current = group;
    const bounds = group.getBounds();
    if (bounds.isValid() && fittedFor.current !== fitKey) {
      map.current.fitBounds(bounds.pad(0.2));
      fittedFor.current = fitKey ?? null;
    }
  }, [data, changes, missingNameLabel, candidates, candidateLabel, editable, selectedFeatureId, fitKey, highlightKey]);

  return <div ref={container} className="map" />;
}
